import React from 'react';
import { formatDate as formatDateUtil } from '../../utils/dateUtils';

/**
 * AdminFamilyDetailsPreview - Read-only admin preview for Family Details form
 * 
 * Renders Family Details form data directly from submission.form_data
 * No routing, no API calls, no submit functionality.
 * Designed for Employee360 modal display.
 */
export default function AdminFamilyDetailsPreview({ submission, readOnly = true }) {
  const formData = submission?.form_data || {};

  const formatDate = (dateString) => {
    return formatDateUtil(dateString, '________________________');
  };

  const currentDate = formatDateUtil(new Date());

  // Create array of 10 rows for family members table
  const familyMembersRows = Array.from({ length: 10 }, (_, index) => {
    return formData.familyMembers && formData.familyMembers[index] 
      ? formData.familyMembers[index] 
      : { name: '', dateOfBirth: '', relationship: '', remarks: '' };
  });

  return (
    <div className="bg-gray-50 py-6 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Document Container */}
        <div className="bg-white border-2 border-black shadow-sm p-8" style={{ minHeight: '1123px' }}>
          {/* Header */}
          <div className="text-center border-b-2 border-black pb-4 mb-6">
            <h1 className="text-xl font-bold text-gray-900">पुणे छावणी परिषद, पुणे - 411001</h1>
            <p className="text-lg font-semibold text-gray-800 mt-2">: परिवार का विवरण :</p>
          </div>

          {/* Employee Details Section */}
          <div className="mb-6">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-semibold text-gray-800">कर्मचारी का नाम :</span>
                <div className="mt-1 border-b border-gray-400 pb-1">
                  <span className="text-gray-900">{formData.employeeName || '________________________'}</span>
                </div>
              </div>
              <div>
                <span className="font-semibold text-gray-800">पदनाम :</span>
                <div className="mt-1 border-b border-gray-400 pb-1">
                  <span className="text-gray-900">{formData.designation || '________________________'}</span>
                </div>
              </div>
              <div>
                <span className="font-semibold text-gray-800">जन्मतिथि :</span>
                <div className="mt-1 border-b border-gray-400 pb-1">
                  <span className="text-gray-900">{formatDate(formData.dateOfBirth)}</span>
                </div>
              </div>
              <div>
                <span className="font-semibold text-gray-800">नियुक्ति की तिथि :</span>
                <div className="mt-1 border-b border-gray-400 pb-1">
                  <span className="text-gray-900">{formatDate(formData.dateOfAppointment)}</span>
                </div>
              </div>
              <div>
                <span className="font-semibold text-gray-800">सेवा निवृति की तिथि दि. :</span>
                <div className="mt-1 border-b border-gray-400 pb-1">
                  <span className="text-gray-900">{formatDate(formData.dateOfRetirement)}</span>
                </div>
              </div>
              <div className="col-span-2">
                <span className="font-semibold text-gray-800">मूल निवासस्थान :</span>
                <div className="mt-1 border-b border-gray-400 pb-1">
                  <span className="text-gray-900 whitespace-pre-line">{formData.permanentAddress || '________________________'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Family Members Table */}
          <div className="mb-6">
            <table className="w-full border-collapse border-2 border-black">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border-2 border-black px-2 py-2 text-center text-xs font-semibold text-gray-800 w-12">
                    क्र.
                  </th>
                  <th className="border-2 border-black px-2 py-2 text-center text-xs font-semibold text-gray-800">
                    परिवार के सदस्यों का नाम
                  </th>
                  <th className="border-2 border-black px-2 py-2 text-center text-xs font-semibold text-gray-800">
                    जन्म तिथि
                  </th>
                  <th className="border-2 border-black px-2 py-2 text-center text-xs font-semibold text-gray-800">
                    कर्मचारी के साथ संबंध
                  </th>
                  <th className="border-2 border-black px-2 py-2 text-center text-xs font-semibold text-gray-800">
                    अभ्युविक्त
                  </th>
                  <th className="border-2 border-black px-2 py-2 text-center text-xs font-semibold text-gray-800">
                    मु. का. अ. के अद्याक्षर
                  </th>
                </tr>
              </thead>
              <tbody>
                {familyMembersRows.map((member, index) => (
                  <tr key={index}>
                    <td className="border-2 border-black px-2 py-2 text-center text-sm text-gray-900 align-middle">
                      {index + 1}
                    </td>
                    <td className="border-2 border-black px-2 py-2 text-sm text-gray-900 align-middle">
                      {member.name || '________________________'}
                    </td>
                    <td className="border-2 border-black px-2 py-2 text-sm text-gray-900 align-middle">
                      {formatDate(member.dateOfBirth)}
                    </td>
                    <td className="border-2 border-black px-2 py-2 text-sm text-gray-900 align-middle">
                      {member.relationship || '________________________'}
                    </td>
                    <td className="border-2 border-black px-2 py-2 text-sm text-gray-900 align-middle">
                      {member.remarks || '________________________'}
                    </td>
                    <td className="border-2 border-black px-2 py-2 align-middle">
                      <div className="h-8"></div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Declaration Section */}
          <div className="mb-6">
            <p className="text-sm text-gray-800 leading-relaxed text-justify">
              मैं द्वारा उपरोक्त विवरण में किसीभी प्रकार के जोड - बदल को मुख्य कार्यकारी अधिकारी के ध्यान में लाते हुए इस विवरण को अद्यावल रखने का वचन देता है।
            </p>
          </div>

          {/* Signature Section */}
          <div className="mb-6">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <span className="text-sm text-gray-800">दिनांक :</span>
                <div className="mt-2 border-b border-gray-400 pb-1">
                  <span className="text-sm text-gray-900">{currentDate}</span>
                </div>
              </div>
              <div className="text-center">
                <span className="text-sm text-gray-800">कर्मचारी के हस्ताक्षर</span>
                <div className="mt-8 border-b border-gray-400 pb-1 inline-block min-w-[150px]">
                  <span className="text-sm text-gray-400">________________________</span>
                </div>
              </div>
              <div className="text-right">
                <span className="text-sm text-gray-800">मुख्यकार्यकारी अधिकारी के अद्याक्षर</span>
                <div className="mt-8 border-b border-gray-400 pb-1 inline-block min-w-[150px]">
                  <span className="text-sm text-gray-400">________________________</span>
                </div>
              </div>
            </div>
          </div>

          {/* Section In-charge Section */}
          <div className="mb-6">
            <div className="text-right">
              <span className="text-sm text-gray-800">अनुभाग प्रभारी के प्रतिहस्ताक्षर</span>
              <div className="mt-8 border-b border-gray-400 pb-1 inline-block min-w-[200px]">
                <span className="text-sm text-gray-400">________________________</span>
              </div>
            </div>
          </div>

          {/* Definition of Family Section */}
          <div className="border-t-2 border-black pt-4">
            <p className="text-sm font-semibold text-gray-800 mb-3">
              इस प्रयोजन के लिए परिवार के अभिप्राय यह है ......
            </p>
            <div className="text-sm text-gray-800 space-y-2">
              <p>1. पुरुष कर्मचारी के मामले में पत्नि।</p>
              <p>2. महिला कर्मचारी के मामले में पति।</p>
              <p>3. पुत्र और अविवाहित पुत्रियां जिसमें वे पुत्र या पुत्रियां भी शामिल है जिन्हें बंद रूप से गोद लिया गया हो।</p>
              <p>4. पिता एवं माता।</p>
              <p>5. 18 वर्ष से कम आयु के भाई जिसमें सौतेले भाई भी शामिल है।</p>
              <p>6. अविवाहित बहने जिसमें सौतेली बहने भी शामिल है।</p>
              <p>7. अन्य कोई अशक्त,</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
